import DataTable from "@/components/data-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useGetInvoices } from "@/hooks/use-subscription";
import { cn } from "@/lib/utils";
import { IInvoice } from "@/types/subscription.types";
import { ColDef } from "ag-grid-community";
import { Download, History } from "lucide-react";
import { useState } from "react";

const colorMap = {
  paid: "dark:bg-green-900/20 bg-green-100 text-green-500 ",
  open: "dark:bg-red-900/20 bg-red-100 text-red-500",
};

const statusMap = {
  paid: "Paid",
  open: "Unpaid",
};

const billingHistoryColumns: ColDef<IInvoice>[] = [
  {
    headerName: "Invoice",
    field: "reference",
    minWidth: 150,
  },
  {
    headerName: "Amount",
    field: "amount",
    minWidth: 150,
    cellRenderer: (params: { value: number | null | undefined }) => {
      const cents = Number(params.value);

      // Check if the value is a valid number
      const isValid = !isNaN(cents);

      const amountInDollars = isValid ? cents / 100 : 0;

      return (
        <div className="h-full flex items-center">
          {isValid
            ? Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format(amountInDollars)
            : "-"}
        </div>
      );
    },
  },
  {
    headerName: "Date",
    field: "createdAt",
    minWidth: 150,
    cellRenderer: (params: { value: string }) => {
      return (
        <div className="h-full flex items-center">
          {params.value
            ? new Date(Number(params.value) * 1000).toLocaleDateString(
                "en-GB",
                {
                  month: "short",
                  day: "2-digit",
                  year: "numeric",
                }
              )
            : "-"}
        </div>
      );
    },
  },
  {
    headerName: "Status",
    field: "status",
    minWidth: 100,
    cellRenderer: (params: { value: string }) => {
      return (
        <div className="h-full flex items-center">
          <Badge
            variant="outline"
            className={cn(
              "border-none capitalize",
              colorMap[params.value as keyof typeof colorMap]
            )}
          >
            {statusMap[params.value as keyof typeof statusMap]}
          </Badge>
        </div>
      );
    },
  },
  {
    headerName: "Actions",
    colId: "payment_actions",
    minWidth: 100,
    filter: false,
    sortable: false,
    resizable: false,
    cellRenderer: (params: { data: IInvoice }) => {
      return (
        <div className="h-full flex items-center">
          {params.data.status === "open" && params.data.hostedInvoiceUrl ? (
            <a
              href={params.data.hostedInvoiceUrl}
              target="_blank"
              onClick={() => {
                window.cioanalytics?.track("customer_portal_opened", {
                  for: "invoice_payment",
                });
              }}
            >
              <Button variant="outline">Make Payment</Button>
            </a>
          ) : (
            "-"
          )}
        </div>
      );
    },
  },
  {
    headerName: "",
    field: "invoicePdf",
    width: 80,
    maxWidth: 80,
    minWidth: 80,
    filter: false,
    sortable: false,
    resizable: false,
    // pinned: "right",
    cellRenderer: (params: { value: string }) => {
      return (
        <div className="h-full flex items-center justify-center">
          <a href={params.value} target="_blank" download>
            <Button variant="ghost" size="icon">
              <Download />
            </Button>
          </a>
        </div>
      );
    },
  },
];

const BillingHistoryTable = () => {
  const [startingAfter, setStartingAfter] = useState<string | undefined>(
    undefined
  );

  const { data: invoicesData, isLoading } = useGetInvoices({
    startingAfter,
  });

  return (
    <section className="space-y-2" id="billing-history">
      <div className="flex flex-col">
        <h2 className="text-lg font-medium">Billing History</h2>
        <p className="text-sm text-muted-foreground">
          View and track your past invoices and payment history
        </p>
      </div>

      {invoicesData?.invoices.length && invoicesData?.invoices.length > 0 ? (
        <div className="flex flex-col gap-2">
          <DataTable
            rowData={invoicesData?.invoices ?? []}
            columnDefs={billingHistoryColumns}
            height={300}
            showPagination={false}
          />
          {invoicesData?.hasMore && invoicesData?.nextCursor && (
            <div className="flex items-center justify-end">
              <Button
                variant="outline"
                disabled={isLoading || !invoicesData?.nextCursor}
                onClick={() => {
                  if (isLoading || !invoicesData?.nextCursor) return;
                  setStartingAfter(invoicesData?.nextCursor);
                }}
              >
                Load More
              </Button>
            </div>
          )}
        </div>
      ) : (
        <Card className="p-6 py-10 flex flex-col items-center justify-center text-center bg-secondary">
          <div className="h-12 aspect-square border rounded-2xl bg-background flex items-center justify-center mb-3">
            <History className="size-6 text-foreground" />
          </div>
          <h3 className="text-base font-medium">No Billing History Yet</h3>
          <p className="text-sm text-muted-foreground max-w-md">
            You haven&apos;t made any transactions yet. Once you start using our
            services, your invoices and payment records will appear here.
          </p>
        </Card>
      )}
    </section>
  );
};

export default BillingHistoryTable;
